import type { DecorationInstance, Footprint, GridCell, GridPosition, PlantState, Season, TerrainType, WeatherState } from '../types';

const clamp = (value: number, minimum = 0, maximum = 100): number => Math.max(minimum, Math.min(maximum, value));

export class GardenGrid {
  width: number;
  height: number;
  private cells: GridCell[];

  constructor(width = 6, height = width, cells?: readonly GridCell[]) {
    if (![6, 8, 10, 12].includes(width) || width !== height) throw new RangeError('Garden grid must be square and 6, 8, 10, or 12 cells');
    this.width = width;
    this.height = height;
    this.cells = cells?.map((cell) => ({ ...cell, occupant: cell.occupant ? { ...cell.occupant } : null })) ?? Array.from({ length: width * height }, (_, index) => this.createCell(index % width, Math.floor(index / width)));
  }

  get all(): readonly GridCell[] { return this.cells; }
  get(x: number, y: number): GridCell | undefined { return this.inBounds(x, y) ? this.cells[y * this.width + x] : undefined; }
  inBounds(x: number, y: number): boolean { return Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < this.width && y < this.height; }

  expand(targetSize: 8 | 10 | 12): { offsetX: number; offsetY: number } {
    if (targetSize <= this.width) throw new RangeError('Target size must be larger than the current grid');
    if (targetSize - this.width !== 2) throw new RangeError('Garden must expand one stage at a time: 6→8→10→12');
    const offset = (targetSize - this.width) / 2;
    const expanded = Array.from({ length: targetSize * targetSize }, (_, index) => this.createCell(index % targetSize, Math.floor(index / targetSize)));
    for (const oldCell of this.cells) {
      const cell: GridCell = { ...oldCell, x: oldCell.x + offset, y: oldCell.y + offset, occupant: oldCell.occupant ? { ...oldCell.occupant } : null };
      expanded[cell.y * targetSize + cell.x] = cell;
    }
    this.width = targetSize;
    this.height = targetSize;
    this.cells = expanded;
    return { offsetX: offset, offsetY: offset };
  }

  setTerrain(x: number, y: number, terrain: TerrainType, elevation?: number): boolean { const cell = this.get(x, y); if (!cell || cell.occupant) return false; cell.terrain = terrain; if (elevation !== undefined) cell.elevation = elevation; return true; }

  canPlace(position: GridPosition, footprint: Footprint, kind: 'plant' | 'decoration'): boolean {
    for (let y = position.y; y < position.y + footprint.h; y += 1) for (let x = position.x; x < position.x + footprint.w; x += 1) {
      const cell = this.get(x, y);
      if (!cell || cell.occupant || cell.terrain === 'water' || cell.terrain === 'pavingStone' || (kind === 'plant' && cell.terrain !== 'soil')) return false;
    }
    return true;
  }

  placePlant(plant: PlantState, footprint: Footprint = { w: 1, h: 1 }): boolean { if (!this.canPlace(plant.gridPosition, footprint, 'plant')) return false; for (const cell of this.area(plant.gridPosition, footprint)) cell.occupant = { kind: 'plant', plantInstanceId: plant.instanceId }; return true; }
  placeDecoration(decoration: DecorationInstance, footprint: Footprint): boolean { if (!this.canPlace(decoration.gridPosition, footprint, 'decoration')) return false; for (const cell of this.area(decoration.gridPosition, footprint)) cell.occupant = { kind: 'decoration', decorationInstanceId: decoration.instanceId }; return true; }

  removeAt(x: number, y: number): boolean {
    const cell = this.get(x, y);
    if (!cell?.occupant) return false;
    const occupant = cell.occupant;
    for (const candidate of this.cells) {
      if (occupant.kind === 'plant' && candidate.occupant?.kind === 'plant' && candidate.occupant.plantInstanceId === occupant.plantInstanceId) candidate.occupant = null;
      if (occupant.kind === 'decoration' && candidate.occupant?.kind === 'decoration' && candidate.occupant.decorationInstanceId === occupant.decorationInstanceId) candidate.occupant = null;
    }
    return true;
  }

  update(elapsedMinutes: number, season: Season, weather: WeatherState, random: () => number = Math.random): void {
    const dt = Math.max(0, elapsedMinutes);
    const previousMoisture = this.cells.map((cell) => cell.soilMoisture);
    for (const cell of this.cells) {
      if (cell.terrain !== 'soil' && cell.terrain !== 'grass') continue;
      const neighbours = [[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dy]) => this.get(cell.x + (dx ?? 0), cell.y + (dy ?? 0))).filter((value): value is GridCell => value !== undefined);
      const average = neighbours.reduce((sum, neighbour) => sum + (previousMoisture[neighbour.y * this.width + neighbour.x] ?? neighbour.soilMoisture), 0) / (neighbours.length || 1);
      let moisture = cell.soilMoisture + (average - cell.soilMoisture) * Math.min(0.22, dt * 0.002);
      const evaporation = 0.0022 * dt * cell.sunlightFactor * (weather.current === 'sunny' ? 1.65 : weather.current === 'windy' ? 1.4 : 0.8);
      moisture += weather.current === 'rainy' ? 0.022 * dt * weather.intensity : -evaporation;
      cell.soilMoisture = clamp(moisture);
      cell.snowDepth = clamp(cell.snowDepth + (weather.current === 'snowy' ? 0.006 * dt * weather.intensity : -0.002 * dt));
      const weedChance = (season === 'summer' ? 0.00012 : 0.00008) * dt * (weather.current === 'rainy' ? 1.7 : 1);
      if (!cell.occupant && !cell.hasWeeds && random() < weedChance) { cell.hasWeeds = true; cell.weedPressure = 5; }
      if (cell.hasWeeds) cell.weedPressure = clamp(cell.weedPressure + dt * 0.002);
    }
  }

  setMulch(position: GridPosition, plant: PlantState, value = true): boolean { const cell = this.get(position.x, position.y); if (!cell || cell.occupant?.kind !== 'plant') return false; plant.mulched = value; return true; }
  setWinterProtection(position: GridPosition, plant: PlantState, value = true): boolean { const cell = this.get(position.x, position.y); if (!cell || cell.occupant?.kind !== 'plant') return false; plant.winterProtected = value; return true; }
  serialize(): GridCell[] { return this.cells.map((cell) => ({ ...cell, occupant: cell.occupant ? { ...cell.occupant } : null })); }

  private area(position: GridPosition, footprint: Footprint): GridCell[] { const cells: GridCell[] = []; for (let y = position.y; y < position.y + footprint.h; y += 1) for (let x = position.x; x < position.x + footprint.w; x += 1) { const cell = this.get(x, y); if (cell) cells.push(cell); } return cells; }
  private createCell(x: number, y: number): GridCell { return { x, y, terrain: 'soil', elevation: 0, occupant: null, soilMoisture: 55, fertility: 50, hasWeeds: false, weedPressure: 0, sunlightFactor: 1, snowDepth: 0, fallenLeaves: 0 }; }
}
